import { InvalidRetryOnFailCallError } from './InvalidRetryOnFailCallError';

export type RetryOnFailParams<T> = {
  /**
   * The delay between retries in milliseconds.
   */
  delay?: number;
  /**
   * The function to call.
   */
  fn: () => Promise<T>;
  /**
   * The maximum number of retries.
   *
   * When set to 0, the function will be called only once and is
   * therefore equivalent to calling the function directly.
   */
  maxRetries: number;
};

export const retryOnFail = async <T>({
  delay = 0,
  fn,
  maxRetries,
}: RetryOnFailParams<T>) => {
  for (let retry = 0; retry <= maxRetries; retry++) {
    try {
      return await fn();
    } catch (error) {
      if (retry >= maxRetries) {
        throw error;
      }

      if (delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Reaching this point should never happen and this
   * error is thrown to help us debug the issue.
   */
  throw new InvalidRetryOnFailCallError(maxRetries);
};
